import { useState, useEffect, useMemo } from "react";
import {
  Checkbox,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Input,
  Show
} from "shared/components";
import { InputSize } from "shared/components/forms/DateInput";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  ArrowUp01Icon,
  Cancel01Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { cx } from "shared/utils";

type MultiSelectDropdownProps = {
  label: string;
  options: string[];
  selectedOptions: string[];
  onSelectionChange: (selected: string[]) => void;
  showSearch?: boolean;
};

export const MultiSelectDropdown = ({
  label,
  options,
  selectedOptions,
  onSelectionChange,
  showSearch = true
}: MultiSelectDropdownProps) => {
  const [open, setOpen] = useState(false);
  const [draftSelected, setDraftSelected] = useState<string[]>(selectedOptions);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    setDraftSelected(selectedOptions);
  }, [selectedOptions]);

  const handleToggle = (option: string) => {
    const newSelection = draftSelected.includes(option)
      ? draftSelected.filter((item) => item !== option)
      : [...draftSelected, option];
    setDraftSelected(newSelection);
    onSelectionChange(newSelection);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDraftSelected([]);
    onSelectionChange([]);
    setSearchQuery("");
  };

  const hasSelection = draftSelected.length > 0;

  const sortedOptions = useMemo(() => {
    return [...options].sort((a, b) => a.localeCompare(b));
  }, [options]);

  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return sortedOptions;
    const query = searchQuery.toLowerCase();
    return sortedOptions.filter((option) =>
      option.toLowerCase().includes(query)
    );
  }, [sortedOptions, searchQuery]);

  // Update parent when draft changes (for immediate feedback, but FilterDrawer Apply will finalize)
  useEffect(() => {
    if (!open) {
      // When closing, sync draft with parent (in case user closed without applying)
      setDraftSelected(selectedOptions);
      setSearchQuery("");
    }
  }, [open, selectedOptions]);

  return (
    <div className="border-b border-alpha-40 pb-4">
      <button
        className="w-full"
        type="button"
        onClick={() => setOpen((prev) => !prev)}
      >
        <div className="flex w-full justify-between items-center">
          <div className="flex items-center gap-2">
            <p className="text-sm font-bold text-content-1000">{label}</p>
            {hasSelection && (
              <span className="text-xs text-content-400">
                ({draftSelected.length})
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 h-[40px]">
            {hasSelection ? (
              <IconButton
                HugeIcon={Cancel01Icon}
                variant={IconButtonVariant.BASE}
                size={IconButtonSize.SM}
                borderLess
                className="!p-0.5 stroke-content-500 shrink-0"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClear(e);
                }}
              />
            ) : null}
            <HugeiconsIcon
              icon={ArrowUp01Icon}
              className={cx("transition-all", open ? "rotate-0" : "rotate-180")}
            />
          </div>
        </div>
      </button>
      <Show if={open}>
        <div className="mt-4 flex flex-col gap-3">
          {showSearch && (
            <Input
              id={`${label.toLowerCase().replace(/\s+/g, "-")}-search`}
              label=""
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="!mb-0"
              size={InputSize.SM}
            />
          )}
          <div className="flex flex-col gap-3 max-h-[200px] overflow-y-auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <Checkbox
                  key={option}
                  id={`${label.toLowerCase().replace(/\s+/g, "-")}-${option}`}
                  label={option}
                  checked={draftSelected.includes(option)}
                  onChange={() => handleToggle(option)}
                />
              ))
            ) : (
              <p className="text-sm text-content-400">No results found</p>
            )}
          </div>
        </div>
      </Show>
    </div>
  );
};
